import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { paths } from '@/app/paths';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

export const ADMIN_MODE_FEATURE_FLAG = 'adminModeEnabled';

const isAdminModePath = (pathname = '') =>
	pathname === paths.admin || pathname.startsWith(`${paths.admin}/`);

/**
 * Administrator plus the adminModeEnabled flag.
 * `flag.active` is the stored global value until FeatureFlagProvider has a user id,
 * so user, broker group, and ledger targeting are checked here as well.
 */
export const isAdminModeEnabled = (flag, userDetails) => {
	if (!userDetails?.isFolioAdmin || !flag) {
		return false;
	}
	if (flag.active) {
		return true;
	}
	if (userDetails.userId && (flag.userIds ?? []).includes(userDetails.userId)) {
		return true;
	}
	const userBrokerGroupIds = userDetails.brokerGroupArray ?? [];
	if ((flag.brokerGroupIds ?? []).some((id) => userBrokerGroupIds.includes(id))) {
		return true;
	}
	const ledger = userDetails.broker?.ledger;
	return Boolean(ledger && (flag.ledgers ?? []).includes(ledger));
};

export const useAdminModeAccess = () => {
	const { pathname } = useLocation();
	const account = useSelector((state) => state.msalAccount.account);
	const firebaseReady = useSelector((state) => state.firebase.firebaseReady);
	const email = account?.username;

	const userQuery = useGetUserDetailsQuery({ email }, { skip: !email || !firebaseReady });

	const {
		getFeatureFlags,
		isLoading: areFlagsLoading,
		isUninitialized: areFlagsUninitialized,
	} = useFeatureFlagContext();

	// A skipped user query stays isUninitialized forever. No email after Firebase is
	// ready means logged out: finished loading, no access.
	const isUserQuerySkipped = !email || !firebaseReady;
	const isAdminModeAccessLoading =
		!firebaseReady ||
		areFlagsLoading ||
		areFlagsUninitialized ||
		(!isUserQuerySkipped && (userQuery.isLoading || userQuery.isUninitialized));

	const canAccessAdminMode =
		!isAdminModeAccessLoading &&
		isAdminModeEnabled(getFeatureFlags(ADMIN_MODE_FEATURE_FLAG), userQuery.data);

	const hasBrokerGroups = (userQuery.data?.brokerGroupArray ?? []).length > 0;
	const isAdminMode = isAdminModePath(pathname);

	return { canAccessAdminMode, isAdminMode, isAdminModeAccessLoading, hasBrokerGroups };
};
